import { Component, OnInit, Inject  } from "@angular/core"
import { CommonModule } from "@angular/common"
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, FormArray, Validators } from "@angular/forms"
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from "@angular/material/dialog"
import { MatButtonModule } from "@angular/material/button"
import { MatIconModule } from "@angular/material/icon"
import {  RatingsService, ManualRatingUpdate } from "../../../services/ratings.service"
import { Station } from "../../../models/station.model"
import { NoScrollInputDirective } from "../../../directives/no-scroll-input.directive"

export interface AddRatingsDialogData {
  station: Station
  currentRatings?: any // Added current ratings for edit mode
  isEdit?: boolean // Added edit mode flag
}

@Component({
  selector: "app-add-ratings-dialog",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    NoScrollInputDirective,
  ],
  templateUrl: "./add-ratings-dialog.component.html",
  styleUrls: ["./add-ratings-dialog.component.css"],
})
export class AddRatingsDialogComponent implements OnInit {
  ratingsForm: FormGroup
  days = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"]
  dayLabels = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"]
  hours = Array.from({ length: 24 }, (_, i) => i)

  selectedDay = "MON"
  bulkRating = 50

  constructor(
    private dialogRef: MatDialogRef<AddRatingsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: AddRatingsDialogData,
    private fb: FormBuilder,
    private ratingsService: RatingsService,
  ) {
    this.ratingsForm = this.fb.group({
      ratings: this.fb.array([]),
    })
  }

  ngOnInit(): void {
    this.initializeForm()
  }

  get ratingsArray(): FormArray {
    return this.ratingsForm.get("ratings") as FormArray
  }

  initializeForm(): void {
    const ratingsArray = this.ratingsArray

    // Initialize with current ratings if in edit mode, otherwise use defaults
    this.days.forEach((day) => {
      this.hours.forEach((hour) => {
        let currentRating = 50 // default value

        if (this.data.isEdit && this.data.currentRatings) {
          const dayKey = day.toLowerCase()
          if (this.data.currentRatings[dayKey] && this.data.currentRatings[dayKey][hour] !== undefined) {
            currentRating = this.data.currentRatings[dayKey][hour]
          }
        }

        ratingsArray.push(
          this.fb.group({
            day: [day],
            hour: [hour],
            rating: [currentRating, [Validators.required, Validators.min(0)]],
          }),
        )
      })
    })
  }

  getRatingControl(day: string, hour: number): FormGroup {
    const index = this.days.indexOf(day) * 24 + hour
    return this.ratingsArray.at(index) as FormGroup   // ✅ FIX: cast properly
  }

  getHourDisplay(hour: number): string {
    return this.ratingsService.getHourDisplay(hour)
  }

  /** Compact header label for dense grid (e.g. 12a, 1p). */
  getHourShort(hour: number): string {
    if (hour === 0) return "12a"
    if (hour === 12) return "12p"
    if (hour < 12) return `${hour}a`
    return `${hour - 12}p`
  }

  applyBulkRating(): void {
    if (this.selectedDay === "ALL") {
      // Apply to all days
      this.ratingsArray.controls.forEach((control) => {
        control.get("rating")?.setValue(this.bulkRating)
      })
    } else {
      // Apply to selected day
      const dayIndex = this.days.indexOf(this.selectedDay)
      for (let hour = 0; hour < 24; hour++) {
        const index = dayIndex * 24 + hour
        this.ratingsArray.at(index).get("rating")?.setValue(this.bulkRating)
      }
    }
  }

  onSubmit(): void {
    if (this.ratingsForm.valid) {
      const updates: ManualRatingUpdate[] = this.ratingsArray.value.map((rating: any) => ({
        stationid: this.data.station.id,
        day: rating.day,
        hour: rating.hour,
        rating: rating.rating,
      }))

      this.dialogRef.close(updates)
    }
  }

  onCancel(): void {
    this.dialogRef.close()
  }
}
